import { useCallback, useEffect, useRef } from 'react';

import Container from '@ui/container/container';
import InputWithOverlay from '@ui/input-with-overlay/input-with-overlay';
import Text from '@ui/text/text/text';
import { Session } from '@/types/session.types';
import SettingsToggle from '../../controls/settings-toggle';
import { useAppDispatch } from '@/store/hooks';
import { saveWorkingProjectSessionByField } from '@/store/slices/project';
import { emptySpace } from '@/utils/empty-space';

interface RedirectUrlDetailProps {
  isEditing: boolean;
  customUrl: string;
  setCustomUrl: React.Dispatch<React.SetStateAction<string>>;
  session?: Session;
  disabled?: boolean;
  invalidUrlError?: string;
}

const RedirectUrlDetail = ({
  isEditing,
  customUrl,
  setCustomUrl,
  disabled = false,
  invalidUrlError = '',
  session,
}: RedirectUrlDetailProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const dispatch = useAppDispatch();

  const customUrlEnabled = session?.custom_url_enabled || false;

  const handleURLChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setCustomUrl(e.target.value);
    },
    [setCustomUrl]
  );

  const handleToggle = useCallback(() => {
    if (!session) return;

    dispatch(
      saveWorkingProjectSessionByField({
        uuid: session.uuid,
        custom_url_enabled: !customUrlEnabled,
      })
    );
  }, [customUrlEnabled, dispatch, session]);

  useEffect(() => {
    if (inputRef.current && isEditing) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  return (
    <>
      <SettingsToggle
        label="Redirect URL"
        value={customUrlEnabled}
        sKey="redirect"
        disabled={disabled}
        onToggle={handleToggle}
      />
      {isEditing && (
        <>
          <Container className="padding-h-16 flex-column gap-20 margin-t--10">
            <Text className="P2" color="var(--global-ico-text-2)">
              All new and existing traffic will be redirected from the session page until this is disabled.
            </Text>
            <InputWithOverlay
              jsxLabel={() => (
                <Text className="L2 padding-l-16 padding-b-4" color="var(--global-ico-text-3)">
                  Redirect URL
                </Text>
              )}
              inputRef={inputRef}
              inputProps={{
                placeholder: 'https://www.example.com',
                className: 'S2',
                value: customUrl,
                onChange: handleURLChange,
                disabled,
              }}
              status={invalidUrlError !== '' ? 'error' : undefined}
            />
          </Container>
          <Container padding="0 16px" margin="4px 0 0 16px" transitionDuration="500ms" className="position-absolute">
            <Text className="C2" color="var(--semantic-red-400)">
              {invalidUrlError || emptySpace}
            </Text>
          </Container>
        </>
      )}
    </>
  );
};

export default RedirectUrlDetail;
